/**
 * モーダルの読み込みとローディング表示を管理するユーティリティクラス
 */
export class ModalLoader {
  /**
   * @param {Object} options - 設定オプション
   * @param {string} options.modalId - モーダルのID
   * @param {string} options.modalFrameId - モーダルフレームのID（turbo_frame_tag）
   * @param {string} options.modalBodyId - モーダル本体のID（コンテンツが読み込まれる場所）
   * @param {string} options.maxWidth - モーダルの最大幅（DaisyUIクラス）デフォルト: 'max-w-2xl'
   * @param {string} options.loadingText - 読み込み中テキスト デフォルト: null（テンプレートのデフォルトを使用）
   */
  constructor(options) {
    this.modalId = options.modalId
    this.modalFrameId = options.modalFrameId
    this.modalBodyId = options.modalBodyId
    this.maxWidth = options.maxWidth || 'max-w-2xl'
    this.loadingText = options.loadingText || null
  }

  /**
   * テンプレートからローディング用のモーダルを生成する
   * @returns {HTMLElement|null}
   */
  getLoadingModalFromTemplate() {
    const template = document.getElementById('modal-loading-template')
    if (!template) {
      return null
    }

    // テンプレートの内容をクローン
    const content = template.content.cloneNode(true)
    const dialog = content.querySelector('dialog')

    if (dialog) {
      // モーダルIDを設定
      dialog.id = this.modalId

      // モーダルボックスに最大幅を設定
      const modalBox = dialog.querySelector('.modal-box')
      if (modalBox) {
        modalBox.classList.add(this.maxWidth)
      }

      // カスタムテキストが指定されている場合は置き換える
      if (this.loadingText) {
        const textElement = dialog.querySelector('[data-loading-text]')
        if (textElement) {
          textElement.textContent = this.loadingText
        }
      }
    }

    return content
  }

  /**
   * 読み込み中表示を表示する
   * モーダルフレーム全体を読み込み中表示で置き換え、前の状態を完全にクリアする
   */
  showLoading() {
    const modalFrame = document.getElementById(this.modalFrameId)
    if (!modalFrame) return

    const loadingContent = this.getLoadingModalFromTemplate()
    modalFrame.innerHTML = ''
    modalFrame.appendChild(loadingContent)
  }

  /**
   * 読み込み中表示を隠す（モーダルを閉じる）
   */
  hideLoading() {
    const modal = document.getElementById(this.modalId)
    if (modal) {
      modal.close()
    }
  }

  /**
   * モーダルを開く
   */
  openModal() {
    const modal = document.getElementById(this.modalId)
    if (modal) {
      // 既に開かれている場合は一度閉じる
      if (modal.open) {
        modal.close()
      }
      modal.showModal?.() || modal.setAttribute('open', 'open')
    }
  }

  /**
   * モーダルを閉じる
   */
  closeModal() {
    const modal = document.getElementById(this.modalId)
    if (modal) {
      modal.close?.() || modal.removeAttribute('open')
    }
  }

  /**
   * 指定されたURLからデータを取得してモーダルに読み込む（Turbo Stream対応）
   * @param {string} url - 取得するURL
   * @param {Object} options - fetchのオプション
   * @returns {Promise<void>}
   */
  async loadWithTurboStream(url, options = {}) {
    this.showLoading()

    try {
      const response = await fetch(url, {
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          ...options.headers
        },
        ...options
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const turboStreamHtml = await response.text()

      // turbo_streamの処理をTurboに委譲
      Turbo.renderStreamMessage(turboStreamHtml)

      // モーダルを開く前に少し待機してDOMの更新を確実にする
      setTimeout(() => {
        this.openModal()
      }, 50)
    } catch (error) {
      console.error('Modal load error:', error)
      this.hideLoading()
      throw error
    }
  }

  /**
   * 指定されたURLからHTMLを取得してモーダル本体に読み込む（通常のHTML対応）
   * @param {string} url - 取得するURL
   * @param {Object} options - fetchのオプション
   * @param {AbortSignal} options.signal - AbortSignal（リクエストのキャンセル用）
   * @returns {Promise<void>}
   */
  async loadWithHtml(url, options = {}) {
    const modalBody = document.getElementById(this.modalBodyId)
    const modal = document.getElementById(this.modalId)

    if (modalBody) {
      // テンプレートからローディングコンテンツを取得
      const loadingContent = this.getLoadingModalFromTemplate()
      // dialogタグの中身（modal-box）だけを取得
      const dialog = loadingContent.querySelector('dialog')
      const modalBox = dialog?.querySelector('.modal-box')
      if (modalBox) {
        modalBody.innerHTML = ''
        // modal-boxの子要素（ローディング表示）をmodalBodyに追加
        while (modalBox.firstChild) {
          modalBody.appendChild(modalBox.firstChild)
        }
      }
    }

    this.openModal()

    try {
      const response = await fetch(url, {
        headers: {
          'Accept': 'text/html',
          'Cache-Control': 'no-cache',
          ...options.headers
        },
        ...options
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const html = await response.text()

      if (modalBody) {
        modalBody.innerHTML = html
      }

      return html
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('Modal load error:', error)
      }
      throw error
    }
  }

  /**
   * モーダル本体をクリアする
   */
  clearModalBody() {
    const modalBody = document.getElementById(this.modalBodyId)
    if (modalBody) {
      modalBody.innerHTML = ''
    }
  }
}

/**
 * よく使われるモーダル設定のファクトリー関数
 */
export const ModalLoaders = {
  /**
   * 編集モーダル用のローダーを作成
   * @param {string} entityName - エンティティ名（例: 'branch', 'employee'）
   * @param {Object} options - 追加のオプション
   */
  createEditModal(entityName, options = {}) {
    return new ModalLoader({
      modalId: `edit_${entityName}_modal`,
      modalFrameId: `edit_${entityName}_modal_frame`,
      modalBodyId: `edit_${entityName}_modal_body`,
      ...options
    })
  },

  /**
   * 新規作成モーダル用のローダーを作成
   * @param {string} entityName - エンティティ名（例: 'branch', 'employee'）
   * @param {Object} options - 追加のオプション
   */
  createNewModal(entityName, options = {}) {
    return new ModalLoader({
      modalId: `new_${entityName}_modal`,
      modalFrameId: `new_${entityName}_modal_frame`,
      modalBodyId: `new_${entityName}_modal_body`,
      ...options
    })
  }
}
